<template>
  <v-card v-if="previews.length || loading" flat class="mt-3 deduction-panel-card">
    <v-layout row align-center class="mb-2">
      <v-icon color="orange darken-1" small class="mr-2">mdi-currency-usd</v-icon>
      <div class="font-weight-medium">Deduction</div>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="16"
        width="2"
        color="orange darken-1"
        class="ml-2"
      ></v-progress-circular>
      <v-spacer></v-spacer>
      <div v-if="previews.length > 1" class="deduction-total">
        <span class="deduction-total-label">Total</span>
        <span class="deduction-total-amount">${{ formatPayMoney(totalAmount) }}</span>
      </div>
    </v-layout>

    <div v-if="!previews.length && loading" class="caption grey--text">
      Calculating deduction…
    </div>

      <div
        v-for="(preview, index) in previews"
        :key="dayKey(preview.date)"
      class="deduction-day-row"
      :class="{ 'mt-3 pt-3': index > 0, 'deduction-day-row--loading': loading }"
    >
      <v-layout row align-center class="mb-1" wrap>
        <span class="font-weight-medium mr-1">{{ formatPayDate(preview.date) }}</span>
        <span v-if="preview.category" class="grey--text">
          — {{ categoryLabel(preview.category) }}
        </span>
        <span v-if="preview.payAbsentCount" class="caption grey--text ml-1">
          — {{ preview.payAbsentCount }} of {{ preview.payRouteCount }} pay routes
        </span>
        <v-chip
          v-if="isBackdated(preview.date)"
          small
          color="orange"
          text-color="white"
          class="ml-2"
        >
          Backdated
        </v-chip>
        <v-chip
          v-if="isChoice(preview.category) && !preview.selectedPreset"
          small
          color="error"
          text-color="white"
          class="ml-2"
        >
          Needs decision
        </v-chip>
        <v-spacer></v-spacer>
        <span v-if="previews.length > 1" class="font-weight-medium">
          ${{ formatPayMoney(preview.amount) }}
        </span>
      </v-layout>
      <div v-if="locked(preview)" class="body-2 mt-1">
        Saved ${{ formatPayMoney(preview.existing.amount) }}
        ({{ preview.existing.preset }})
        <span v-if="preview.existing.enteredBy" class="grey--text">
          — entered by {{ preview.existing.enteredBy }}
        </span>
      </div>
      <v-radio-group
        v-else
        v-model="preview.selectedPreset"
        hide-details
        class="mt-0 deduction-preset-group"
        @change="onPayPresetChanged(preview)"
      >
        <v-radio
          v-for="suggestion in preview.suggestions.filter((s) => s.preset !== 'NO_DEDUCT')"
          :key="dayKey(preview.date) + suggestion.preset"
          :value="suggestion.preset"
          color="success"
          :label="suggestion.label + ' — $' + formatPayMoney(suggestion.amount)"
        ></v-radio>
        <v-radio
          value="MANUAL"
          color="success"
          label="Custom amount"
        ></v-radio>
        <v-radio
          v-for="suggestion in preview.suggestions.filter((s) => s.preset === 'NO_DEDUCT')"
          :key="dayKey(preview.date) + suggestion.preset"
          :value="suggestion.preset"
          color="success"
          :label="suggestion.label + ' — $' + formatPayMoney(suggestion.amount)"
        ></v-radio>
      </v-radio-group>
      <v-text-field
        v-if="!locked(preview) && preview.selectedPreset === 'MANUAL'"
        v-model="preview.customAmount"
        label="Amount"
        type="number"
        prefix="$"
        class="mt-1"
        @input="onCustomAmount(preview)"
      ></v-text-field>
      <v-text-field
        v-if="!locked(preview) && preview.selectedPreset === 'MANUAL'"
        v-model="preview.customReason"
        label="Reason (optional)"
        class="mt-1"
      ></v-text-field>
      <v-text-field
        v-if="!locked(preview) && preview.selectedPreset === 'NO_DEDUCT'"
        v-model="preview.noDeductReason"
        label="Reason for no deduct"
        :rules="[rules.required]"
        class="mt-1"
      ></v-text-field>
      <v-layout v-if="!locked(preview)" row class="mt-2">
        <div class="mr-3">
          <div class="caption grey--text mb-1">Absent from</div>
          <DxDateBox
            type="time"
            picker-type="list"
            :interval="15"
            :width="150"
            :value="asTimeValue(preview.timeFrom)"
            :show-clear-button="true"
            @value-changed="onTimeChanged(preview, 'timeFrom', $event)"
          />
        </div>
        <div>
          <div class="caption grey--text mb-1">Absent to</div>
          <DxDateBox
            type="time"
            picker-type="list"
            :interval="15"
            :width="150"
            :value="asTimeValue(preview.timeTo)"
            :show-clear-button="true"
            @value-changed="onTimeChanged(preview, 'timeTo', $event)"
          />
        </div>
      </v-layout>
      <div v-if="!locked(preview)" class="caption grey--text mt-1">
        Saved with this deduction. Record the whole time the driver was out,
        even when a trip only covers part of it.
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import DxDateBox from "devextreme-vue/date-box";

export default {
  components: {
    DxDateBox,
  },
  props: {
    driverBusId: {
      type: [Number, String],
      default: null,
    },
    dates: {
      type: Array,
      default: () => [],
    },
    kind: {
      type: String,
      default: "",
    },
    // route IDs currently marked absent (the "pay routes" selection in the report dialog)
    routeIds: {
      type: Array,
      default: () => [],
    },
    // true when this absence has no routes attached (a person-level absent)
    personAbsent: {
      type: Boolean,
      default: null,
    },
    allowEditExisting: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      previews: [],
      loading: false,
      userPicked: false,
      timeValues: {},
      refreshToken: 0,
      refreshDebounce: null,
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  computed: {
    totalAmount() {
      return this.previews.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    },
  },
  watch: {
    driverBusId() {
      this.userPicked = false;
      this.scheduleRefresh();
    },
    dates: {
      handler() {
        this.userPicked = false;
        this.scheduleRefresh();
      },
      deep: true,
    },
    kind() {
      this.userPicked = false;
      this.scheduleRefresh();
    },
    routeIds: {
      handler() {
        this.userPicked = false;
        this.scheduleRefresh();
      },
      deep: true,
    },
  },
  methods: {
    dayKey(date) {
      if (!date) return "";
      const parsed = moment(date);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(date).substr(0, 10);
    },
    asTimeValue(value) {
      if (!value) return null;
      if (moment.isDate(value)) {
        return moment(value).isValid() ? value : null;
      }
      const parsed = moment(value, ["h:mm A", "HH:mm", "HH:mm:ss"], true);
      return parsed.isValid() ? parsed.toDate() : null;
    },
    parsePayTime(value) {
      if (!value) return null;
      const parsed = moment.isDate(value)
        ? moment(value)
        : moment(value, ["h:mm A", "HH:mm", "HH:mm:ss"], true);
      return parsed.isValid() ? parsed.format("HH:mm:ss") : null;
    },
    isBackdated(date) {
      if (!date) return false;
      return moment(date).isBefore(moment(), "day");
    },
    // Called on every relevant prop change. Debounced so a burst of rapid clicks
    // (e.g. toggling AM then PM) only fires one request instead of one per click.
    scheduleRefresh() {
      if (this.refreshDebounce) {
        clearTimeout(this.refreshDebounce);
        this.refreshDebounce = null;
      }

      if (!this.driverBusId || !this.dates.length) {
        this.refreshToken++;
        this.loading = false;
        this.previews = [];
        this.$emit("input", this.previews);
        return;
      }

      // Show the spinner right away so the UI feels responsive even while we wait
      // out the debounce window and the request itself.
      this.loading = true;
      this.refreshDebounce = setTimeout(() => {
        this.refreshDebounce = null;
        this.refresh();
      }, 200);
    },
    async refresh() {
      if (!this.driverBusId || !this.dates.length) {
        this.previews = [];
        return;
      }

      // Any refresh started after this one invalidates it, so a slow response can
      // never overwrite what a later, faster response already rendered.
      const token = ++this.refreshToken;

      const previous = {};
      this.previews.forEach((p) => {
        previous[this.dayKey(p.date)] = p;
      });

      this.loading = true;
      try {
        const responses = await Promise.all(
          this.dates.map((date) => {
            const prior = previous[this.dayKey(date)] || {};
            return this.$http.post("api/driverAbsent/previewDeduction", {
              driverBusID: this.driverBusId,
              date: date,
              kind: this.kind,
              absentRouteIDs: this.routeIds,
              personAbsent:
                this.personAbsent === null
                  ? !this.routeIds.length
                  : this.personAbsent,
              timeFrom: this.parsePayTime(prior.timeFrom),
              timeTo: this.parsePayTime(prior.timeTo),
            });
          })
        );

        if (token !== this.refreshToken) return; // superseded by a newer refresh

        this.previews = responses.map((preview, index) => {
          const date = this.dayKey(this.dates[index]);
          const prior = previous[date] || {};
          const suggestions = preview.suggestions || [];
          const locked =
            !this.allowEditExisting &&
            preview.existing &&
            preview.existing.deskOverride;
          const priorStillOffered =
            this.userPicked &&
            (prior.selectedPreset === "MANUAL" ||
              suggestions.some((s) => s.preset === prior.selectedPreset));
          const selectedPreset = locked
            ? preview.existing.preset
            : priorStillOffered
            ? prior.selectedPreset
            : preview.recommendedPreset || "NO_DEDUCT";
          const selected = suggestions.find(
            (s) => s.preset === selectedPreset
          );
          const customAmount =
            prior.customAmount != null ? prior.customAmount : "";
          const customReason = prior.customReason || "";
          return {
            date: date,
            category: preview.category || "",
            personAbsent: !!preview.personAbsent,
            payRouteCount: preview.payRouteCount || 0,
            payAbsentCount: preview.payAbsentCount || 0,
            suggestions: preview.suggestions || [],
            selectedPreset: selectedPreset,
            customAmount: customAmount,
            customReason: customReason,
            amount: locked
              ? Number(preview.existing.amount) || 0
              : selectedPreset === "MANUAL"
              ? Number(customAmount) || 0
              : selected
              ? selected.amount
              : preview.recommendedAmount || 0,
            noDeductReason: prior.noDeductReason || "",
            timeFrom: this.keptTime(
              date,
              "timeFrom",
              prior.timeFrom || (preview.existing && preview.existing.timeFrom)
            ),
            timeTo: this.keptTime(
              date,
              "timeTo",
              prior.timeTo || (preview.existing && preview.existing.timeTo)
            ),
            existing: preview.existing || null,
          };
        });

        this.$emit("input", this.previews);
      } catch (e) {
        if (token !== this.refreshToken) return; // superseded by a newer refresh
        this.previews = [];
        this.$emit("input", this.previews);
      } finally {
        if (token === this.refreshToken) {
          this.loading = false;
        }
      }
    },
    keptTime(date, field, fallback) {
      const key = date + "|" + field;
      if (Object.prototype.hasOwnProperty.call(this.timeValues, key)) {
        return this.timeValues[key];
      }
      return this.asTimeValue(fallback);
    },
    onTimeChanged(preview, field, event) {
      if (this.loading) return;
      const value = event && event.value !== undefined ? event.value : null;
      const next = this.asTimeValue(value);
      if (!next && !(event && event.event)) return;
      const key = this.dayKey(preview.date) + "|" + field;
      if (this.parsePayTime(this.timeValues[key]) === this.parsePayTime(next)) {
        return;
      }
      this.$set(this.timeValues, key, next);
      preview[field] = next;
    },
    onCustomAmount(preview) {
      preview.amount = Number(preview.customAmount) || 0;
    },
    locked(preview) {
      return (
        !this.allowEditExisting &&
        preview.existing &&
        preview.existing.deskOverride
      );
    },
    isChoice(category) {
      return (
        category === "CUSTOM" ||
        category === "AM+CUSTOM" ||
        category === "PM+CUSTOM"
      );
    },
    categoryLabel(category) {
      if (category === "AM+CUSTOM") return "AM + CUSTOM";
      if (category === "PM+CUSTOM") return "PM + CUSTOM";
      return category || "";
    },
    onPayPresetChanged(preview) {
      if (this.loading) return;
      this.userPicked = true;
      if (preview.selectedPreset === "MANUAL") {
        preview.amount = Number(preview.customAmount) || 0;
        return;
      }
      const selected = (preview.suggestions || []).find(
        (s) => s.preset === preview.selectedPreset
      );
      preview.amount = selected ? selected.amount : 0;
    },
    formatPayDate(date) {
      return date ? moment(date).format("M/D/YYYY") : "";
    },
    formatPayMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    formatClock(value) {
      if (!value) return "";
      const parsed = moment(value, ["HH:mm", "h:mm A", "HH:mm:ss"], true);
      return parsed.isValid() ? parsed.format("h:mm A") : value;
    },
    // Returns false (and shows a notification) when a NO_DEDUCT selection is missing its reason.
    validate() {
      const editable = this.previews.filter((p) => !this.locked(p));
      if (
        editable.some(
          (p) => p.selectedPreset === "NO_DEDUCT" && !p.noDeductReason
        )
      ) {
        this.$notify({
          text: "A reason is required when not deducting",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        editable.some(
          (p) =>
            p.selectedPreset === "MANUAL" &&
            (p.customAmount === "" ||
              p.customAmount == null ||
              isNaN(Number(p.customAmount)))
        )
      ) {
        this.$notify({
          text: "Enter the custom amount",
          group: "notification",
          type: "error",
        });
        return false;
      }

      return true;
    },
    // Saves a DeskOverride=true deduction row for every date currently previewed.
    async persist(driverId, routeIds, kind, personAbsent) {
      if (!driverId || !this.previews.length) return;

      const effectiveRouteIds = routeIds || this.routeIds;
      const effectiveKind =
        kind !== undefined ? kind : effectiveRouteIds.length ? this.kind : "";
      const effectivePersonAbsent =
        personAbsent === undefined ? !effectiveRouteIds.length : personAbsent;

      const rows = this.previews.filter((preview) => !this.locked(preview));
      if (!rows.length) return;

      await Promise.all(
        rows.map((preview) =>
          this.$http.post("api/driverAbsent/saveDeduction", {
            driverBusID: driverId,
            date: preview.date,
            kind: effectiveKind,
            category: preview.category,
            preset: preview.selectedPreset,
            amount: preview.amount,
            noDeductReason:
              preview.selectedPreset === "MANUAL"
                ? preview.customReason
                : preview.noDeductReason,
            timeFrom: this.parsePayTime(preview.timeFrom),
            timeTo: this.parsePayTime(preview.timeTo),
            updateOutWindow: true,
            absentRouteIDs: effectiveRouteIds,
            personAbsent: effectivePersonAbsent,
            enteredBy: this.$store.state.authentication.user,
          })
        )
      );
    },
    reset() {
      if (this.refreshDebounce) {
        clearTimeout(this.refreshDebounce);
        this.refreshDebounce = null;
      }
      this.refreshToken++;
      this.loading = false;
      this.userPicked = false;
      this.previews = [];
    },
  },
};
</script>
<style scoped>
.deduction-panel-card.v-card {
  background-color: #fff8f0 !important;
  border: 1px solid #ffe0b2 !important;
  border-left: 4px solid #fb8c00 !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  padding: 12px 16px;
}
.deduction-day-row + .deduction-day-row {
  border-top: 1px solid #ffe0b2;
}
.deduction-day-row--loading {
  opacity: 0.5;
  pointer-events: none;
}
.deduction-total {
  display: flex;
  align-items: baseline;
  background-color: #fb8c00;
  color: #fff;
  border-radius: 4px;
  padding: 2px 10px;
}
.deduction-total-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-right: 6px;
  opacity: 0.9;
}
.deduction-total-amount {
  font-size: 18px;
  font-weight: 700;
}
</style>
